import { CssBaseline, ThemeProvider } from '@mui/material'
import { lazy, Suspense, useEffect } from 'react'

import { Toaster } from 'react-hot-toast'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import '@fontsource/roboto/100.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
// Platform brand heading faces (theme/brands.js). @font-face only downloads a
// face once text uses it, so a host on one brand never fetches the other's.
import '@fontsource-variable/bricolage-grotesque'
import '@fontsource-variable/manrope'
import { ErrorBoundary } from 'react-error-boundary'

import './locales/i18n'
import LoadingScreen from '@/components/LoadingScreen'
import RootErrorFallback from '@/components/RootErrorFallback'
import RTL from '@/components/RTL'
import SplashScreen from '@/components/SplashScreen'
import SplashScreenPower from '@/components/SplashScreenPower'
import { useBranding } from '@/contexts/BrandingContext'
import OnboardingDialog from '@/features/account/first-run/OnboardingDialog'
import {
    isMfaSetupAllowedPath,
    needsMfaSetup
} from '@/features/allauth/lib/mfaPolicy'
import SettingsDrawer from '@/features/settings-drawer/SettingsDrawer'
import useAuth from '@/hooks/useAuth'
import usePageViewTracking from '@/hooks/usePageViewTracking'
import useScrollReset from '@/hooks/useScrollReset'
import { reportError } from '@/lib/faro'
import { isFettesHost } from '@/lib/host'
import { effectiveStyle } from '@/lib/visitorStyle'
import { createTheme } from '@/theme'
import { brandLook } from '@/theme/brands'
import { createEmotionCache } from '@/utils/createEmotionCache'
import '@/style.css'

import AuthGuard from '@/features/authentication/AuthGuard'
import GuestGuard from '@/features/authentication/GuestGuard'
import MfaSetupGuard from '@/features/authentication/MfaSetupGuard'
import OrgAccessGuard from '@/features/authentication/OrgAccessGuard'
import RoleGuard from '@/features/authentication/RoleGuard'
import SignupEnabledGuard from '@/features/authentication/SignupEnabledGuard'
import BlogLayout from '@/layout/blog/BlogLayout'
import BrowseLayout from '@/layout/browse/BrowseLayout'
import DashboardLayout from '@/layout/dashboard/DashboardLayout'
import DocsLayout from '@/layout/docs/DocsLayout'
import MainLayout from '@/layout/main/MainLayout'
import {
    LEGAL_PAGES_READY,
    PRIVACY_POLICY,
    TERMS_OF_SERVICE
} from '@/pages/legal/legalContent.js'

import { routeConfig } from '@/routeConfig'
import { buildPublicRoutes } from '@/routeUtils'

const Loadable = (Component) => (props) => (
    <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
    </Suspense>
)

// Static pages (not part of dashboard route config)
const Home = Loadable(lazy(() => import('@/pages/Home')))
const SegmentPage = Loadable(
    lazy(() => import('@/pages/marketing/SegmentPage'))
)
const FeaturesPage = Loadable(
    lazy(() => import('@/pages/marketing/FeaturesPage'))
)
const Checkout = Loadable(lazy(() => import('@/pages/Checkout')))
const Contact = Loadable(lazy(() => import('@/pages/Contact')))
const LegalPage = Loadable(lazy(() => import('@/pages/legal/LegalPage')))
const AuthorizationRequired = Loadable(lazy(() => import('@/pages/401')))
// Note: Login (allauth) and PermissionRequired (403) are also statically imported by
// AuthGuard / RoleGuard. Vite emits a "dynamic import will not move module into
// another chunk" warning — it's safe to ignore; eagerly importing them at module
// top-level surfaced a circular-import TDZ error in production.
const PermissionRequired = Loadable(lazy(() => import('@/pages/403')))
const NotFound = Loadable(lazy(() => import('@/pages/404')))
const ServerError = Loadable(lazy(() => import('@/pages/500')))
const LoginError = Loadable(lazy(() => import('@/pages/LoginError')))

// Allauth social & MFA pages
// Old
// const Login = Loadable(lazy(() => import('@/pages/authentication/Login')))
// const PasswordRecovery = Loadable(lazy(() => import('@/pages/authentication/PasswordRecovery')))
// const PasswordReset = Loadable(lazy(() => import('@/pages/authentication/PasswordReset')))
// const Register = Loadable(lazy(() => import('@/pages/authentication/Register')))
// const RegisterSuccess = Loadable(lazy(() => import('@/pages/authentication/RegisterSuccess')))
// const VerifyEmail = Loadable(lazy(() => import('@/pages/authentication/VerifyEmail')))
// const VerifyEmailSent = Loadable(lazy(() => import('@/pages/authentication/VerifyEmailSent')))
// Allauth — Account
const Login = Loadable(
    lazy(() => import('@/features/allauth/pages/account/Login'))
)
const PasswordRecovery = Loadable(
    lazy(() => import('@/features/allauth/pages/account/PasswordRecovery'))
)
const PasswordReset = Loadable(
    lazy(() => import('@/features/allauth/pages/account/PasswordReset'))
)
const Signup = Loadable(
    lazy(() => import('@/features/allauth/pages/account/Signup'))
)
const RegisterSuccess = Loadable(
    lazy(() => import('@/features/allauth/pages/account/RegisterSuccess'))
)
// Invitation acceptance. Public by necessity — the invitee has no session yet, and creating
// one is the point — so this sits outside AuthGuard and outside the VITE_USE_EXTERNAL_AUTH
// conditional that wraps the password-login routes.
const AcceptInvite = Loadable(
    lazy(() => import('@/features/allauth/pages/account/AcceptInvite'))
)
const VerifyEmail = Loadable(
    lazy(() => import('@/features/allauth/pages/account/VerifyEmail'))
)
const VerifyEmailResend = Loadable(
    lazy(() => import('@/features/allauth/pages/account/VerifyEmailSent'))
)
const Reauthenticate = Loadable(
    lazy(() => import('@/features/allauth/pages/account/Reauthenticate'))
)

// Allauth — Social Account
const ProviderCallback = Loadable(
    lazy(
        () => import('@/features/allauth/pages/socialaccount/ProviderCallback')
    )
)
const ProviderSignup = Loadable(
    lazy(() => import('@/features/allauth/pages/socialaccount/ProviderSignup'))
)
const ManageProviders = Loadable(
    lazy(() => import('@/features/allauth/pages/socialaccount/ManageProviders'))
)

// Allauth — MFA. Management lives on the Account page's Security tab
// (/account/security, MFAOverview); these are the standalone steps: choosing
// and setting up an authenticator app or a security key or passkey, recovery
// codes, and the second step of a password sign-in.
const MfaSetupChoice = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/MfaSetupChoice'))
)
const AddWebAuthn = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/AddWebAuthn'))
)
const AuthenticateWebAuthn = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/AuthenticateWebAuthn'))
)
const ActivateTOTP = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/ActivateTOTP'))
)
const AuthenticateTOTP = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/AuthenticateTOTP'))
)
const DeactivateTOTP = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/DeactivateTOTP'))
)
const AllauthRecoveryCodes = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/RecoveryCodes'))
)
const AuthenticateRecoveryCodes = Loadable(
    lazy(() => import('@/features/allauth/pages/mfa/AuthenticateRecoveryCodes'))
)

const clientSideEmotionCache = createEmotionCache()

// Partner colours are free-text on the backend; only well-formed hex reaches
// the theme so a typo'd brand colour degrades to the user's preset, not a crash.
const HEX_COLOUR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i
const asBrandColour = (value) =>
    HEX_COLOUR.test(value?.trim() ?? '') ? value.trim() : undefined

const App = () => {
    const { isAuthenticated, isInitialized, settings, user } = useAuth()
    const { brand } = useBranding()
    const { pathname } = useLocation()

    useScrollReset()
    usePageViewTracking()

    const style = effectiveStyle(settings.style, {
        isInitialized,
        isAuthenticated,
        isFettes: isFettesHost()
    })
    const theme = createTheme({
        brandPrimary: asBrandColour(brand.primary_color),
        brandSecondary: asBrandColour(brand.secondary_color),
        brandHeadingFont: brandLook(brand.brand_key).headingFont,
        fallbackSecondary: style.secondary,
        colorPreset: style.colorPreset,
        containerStretch: style.containerStretch,
        contrast: style.contrast,
        direction: style.direction,
        layout: style.layout,
        navColor: style.navColor,
        roundedCorners: style.roundedCorners,
        responsiveFontSizes: style.responsiveFontSizes,
        theme: style.theme
    })

    useEffect(() => {
        document.body.style.backgroundColor = theme.palette.background.default
    }, [theme.palette.background.default])

    return (
        <ErrorBoundary
            FallbackComponent={RootErrorFallback}
            onError={(error, info) =>
                reportError(error, {
                    label: 'app-root',
                    componentStack: info?.componentStack
                })
            }
        >
            <ThemeProvider theme={theme}>
                <RTL direction={settings.style.direction}>
                    <CssBaseline />
                    <Toaster position="top-center" />

                    {/* Not over the two-factor pages either: straight after
                        setup, the recovery codes must be read and saved first. */}
                    {isInitialized &&
                        isAuthenticated &&
                        !needsMfaSetup(user) &&
                        !isMfaSetupAllowedPath(pathname) && (
                            <OnboardingDialog />
                        )}
                    {isInitialized ? (
                        <Routes>
                            {isAuthenticated && (
                                <Route
                                    path="/*"
                                    element={
                                        <AuthGuard>
                                            <MfaSetupGuard>
                                                <OrgAccessGuard>
                                                    <DashboardLayout />
                                                </OrgAccessGuard>
                                            </MfaSetupGuard>
                                        </AuthGuard>
                                    }
                                />
                            )}

                            {!isAuthenticated && (
                                <Route path="/" element={<MainLayout />}>
                                    <Route index element={<Home />} />
                                    {buildPublicRoutes(routeConfig)}
                                    {/* Marketing spokes are declared here rather than through
                                        routeConfig/buildPublicRoutes, which returns [] under
                                        VITE_BETA_MODE — that would 404 these URLs instead of
                                        degrading to the homepage. SegmentPage and FeaturesPage
                                        decide what to do (render / redirect) via
                                        resolveSegmentRoute / resolveFeaturesRoute. */}
                                    <Route
                                        path="for/:segmentKey"
                                        element={<SegmentPage />}
                                    />
                                    <Route
                                        path="features"
                                        element={<FeaturesPage />}
                                    />
                                    <Route
                                        path="browse/*"
                                        element={<BrowseLayout />}
                                    />
                                    {/* Inside MainLayout so "Book a demo" keeps the site's
                                        navbar; signed-in users get the bare page below. */}
                                    <Route
                                        path="contact/*"
                                        element={<Contact />}
                                    />
                                    {LEGAL_PAGES_READY && (
                                        <>
                                            <Route
                                                path="privacy"
                                                element={
                                                    <LegalPage
                                                        document={
                                                            PRIVACY_POLICY
                                                        }
                                                    />
                                                }
                                            />
                                            <Route
                                                path="terms"
                                                element={
                                                    <LegalPage
                                                        document={
                                                            TERMS_OF_SERVICE
                                                        }
                                                    />
                                                }
                                            />
                                        </>
                                    )}
                                </Route>
                            )}

                            {/* A signed-in user following a /for/... or /features link from an
                                email would otherwise fall through to DashboardLayout's "/*" and
                                hit its 404. A static first segment outranks that splat, so these
                                win. */}
                            {isAuthenticated && (
                                <>
                                    <Route
                                        path="for/*"
                                        element={<Navigate to="/" replace />}
                                    />
                                    <Route
                                        path="features"
                                        element={<Navigate to="/" replace />}
                                    />
                                </>
                            )}

                            {import.meta.env.VITE_USE_EXTERNAL_AUTH ===
                                'false' && (
                                <>
                                    <Route
                                        path="login"
                                        element={
                                            <GuestGuard>
                                                <Login />
                                            </GuestGuard>
                                        }
                                    />
                                    {/* Password recovery is for password accounts. An
                                        SSO org's people are told on the recovery page
                                        to reset with their identity provider, and the
                                        backend withholds the link from them as well
                                        (AllauthCustomAdapter.send_password_reset_mail).
                                        password-reset/:key stays unguarded: a link
                                        from an email must work in whatever state the
                                        browser happens to be in. */}
                                    <Route
                                        path="password-recovery"
                                        element={
                                            <GuestGuard>
                                                <PasswordRecovery />
                                            </GuestGuard>
                                        }
                                    />
                                    <Route
                                        path="password-reset/:key"
                                        element={<PasswordReset />}
                                    />
                                    {/* self-serve-org-signup: public signup, reachable
                                        while logged out, leading to email verification.
                                        register / register-success / resend-verification
                                        are the signup front door — hidden behind
                                        SignupEnabledGuard when self-serve signup is off
                                        (redirects to /login rather than a dead page).
                                        verify-email/:key stays unwrapped: someone
                                        mid-verification when the flag flips off must
                                        still be able to finish. */}
                                    <Route
                                        path="register"
                                        element={
                                            <SignupEnabledGuard>
                                                <GuestGuard>
                                                    <Signup />
                                                </GuestGuard>
                                            </SignupEnabledGuard>
                                        }
                                    />
                                    <Route
                                        path="register-success"
                                        element={
                                            <SignupEnabledGuard>
                                                <RegisterSuccess />
                                            </SignupEnabledGuard>
                                        }
                                    />
                                    <Route
                                        path="verify-email/:key"
                                        element={<VerifyEmail />}
                                    />
                                    <Route
                                        path="resend-verification"
                                        element={
                                            <SignupEnabledGuard>
                                                <VerifyEmailResend />
                                            </SignupEnabledGuard>
                                        }
                                    />
                                </>
                            )}

                            {/* Allauth: Social provider callback & signup */}
                            <Route
                                path="account/provider/callback"
                                element={<ProviderCallback />}
                            />
                            {/* Allauth: two-factor authentication. /account/mfa is the
                                old address of the overview, which now lives on the
                                Account page's Security tab. */}
                            <Route
                                path="account/mfa"
                                element={
                                    <Navigate to="/account/security" replace />
                                }
                            />
                            <Route
                                path="account/mfa/setup"
                                element={
                                    <AuthGuard>
                                        <MfaSetupChoice />
                                    </AuthGuard>
                                }
                            />
                            <Route
                                path="account/mfa/totp/activate"
                                element={
                                    <AuthGuard>
                                        <ActivateTOTP />
                                    </AuthGuard>
                                }
                            />
                            <Route
                                path="account/mfa/webauthn/add"
                                element={
                                    <AuthGuard>
                                        <AddWebAuthn />
                                    </AuthGuard>
                                }
                            />
                            <Route
                                path="account/mfa/totp/deactivate"
                                element={
                                    <AuthGuard>
                                        <DeactivateTOTP />
                                    </AuthGuard>
                                }
                            />
                            <Route
                                path="account/mfa/recovery-codes"
                                element={
                                    <AuthGuard>
                                        <AllauthRecoveryCodes />
                                    </AuthGuard>
                                }
                            />
                            <Route
                                path="account/reauthenticate"
                                element={
                                    <AuthGuard>
                                        <Reauthenticate />
                                    </AuthGuard>
                                }
                            />
                            {/*<Route path="account/provider/signup" element={<ProviderSignup />} />*/}
                            {/*<Route path="account/providers" element={<AuthGuard><ManageProviders /></AuthGuard>} />*/}

                            {/* Allauth: the second step of a password sign-in. No
                                AuthGuard: the user is not signed in until it is done. */}
                            <Route
                                path="account/mfa/authenticate/totp"
                                element={<AuthenticateTOTP />}
                            />
                            <Route
                                path="account/mfa/authenticate/webauthn"
                                element={<AuthenticateWebAuthn />}
                            />
                            <Route
                                path="account/mfa/authenticate/recovery-codes"
                                element={<AuthenticateRecoveryCodes />}
                            />

                            {/* Docs are open to site admins and partner admins.
                                The docs sidebar role-filters which sections each
                                sees (partner admins only get the Partner guide).
                                AuthGuard first so logged-out users are sent to
                                login rather than hitting RoleGuard with no user. */}
                            <Route
                                path="/docs/*"
                                element={
                                    <AuthGuard>
                                        <RoleGuard
                                            roles={[
                                                'Site Admin Role',
                                                'Partner Admin Role'
                                            ]}
                                        >
                                            <DocsLayout />
                                        </RoleGuard>
                                    </AuthGuard>
                                }
                            />
                            <Route path="checkout" element={<Checkout />} />
                            <Route
                                path="401"
                                element={<AuthorizationRequired />}
                            />
                            <Route
                                path="403"
                                element={<PermissionRequired />}
                            />
                            <Route path="404" element={<NotFound />} />
                            <Route path="500" element={<ServerError />} />
                            <Route
                                path="user-login-problem"
                                element={<LoginError />}
                            />
                            <Route
                                path="invite/:token"
                                element={<AcceptInvite />}
                            />
                            <Route path="blog/*" element={<BlogLayout />} />
                            {isAuthenticated && (
                                <Route
                                    path="/contact/*"
                                    element={<Contact />}
                                />
                            )}
                            {isAuthenticated && LEGAL_PAGES_READY && (
                                <>
                                    <Route
                                        path="/privacy"
                                        element={
                                            <LegalPage
                                                document={PRIVACY_POLICY}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/terms"
                                        element={
                                            <LegalPage
                                                document={TERMS_OF_SERVICE}
                                            />
                                        }
                                    />
                                </>
                            )}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    ) : (
                        <SplashScreenPower />
                    )}
                </RTL>
            </ThemeProvider>
        </ErrorBoundary>
    )
}

export default App
